import React, { useMemo } from 'react';
import { Modal, Table, Button, Badge } from 'react-bootstrap';
import { formatCurrency } from '../../utils/formatters';

const StatementSummaryModal = ({ 
  show, 
  onHide, 
  onConfirm, 
  summaryTargets, 
  pdfTransactions,
  reconcileData 
}) => {
  
  const comparison = useMemo(() => {
    if (!summaryTargets) return [];

    const calculateParsed = (sectionKey) => {
      const filtered = pdfTransactions.filter(t => t.section === sectionKey);
      return {
        count: filtered.length,
        amount: filtered.reduce((sum, t) => sum + t.amount, 0)
      };
    };

    const sections = [
      { label: 'Deposits and Additions', key: 'deposits', baseline: summaryTargets.deposits },
      { label: 'Checks Paid', key: 'checks', baseline: summaryTargets.checks },
      { label: 'ATM & Debit Card Withdrawals', key: 'withdrawals', baseline: summaryTargets.withdrawals },
      { label: 'Electronic Withdrawals', key: 'electronic', baseline: summaryTargets.electronic },
      { label: 'Fees', key: 'fees', baseline: summaryTargets.fees },
      { label: 'Other Withdrawals', key: 'other', baseline: summaryTargets.other }
    ];

    return sections.map(s => {
      const parsed = calculateParsed(s.key);
      const baseline = s.baseline || { count: 0, amount: 0 };
      
      return {
        ...s,
        baseline,
        parsed,
        diffCount: parsed.count - baseline.count,
        diffAmount: parsed.amount - baseline.amount
      };
    }).filter(s => s.baseline.count > 0 || s.parsed.count > 0);
  }, [summaryTargets, pdfTransactions]);

  const hasDiscrepancy = useMemo(() => {
    const totalAmountDiff = comparison.reduce((sum, s) => sum + Math.abs(s.diffAmount), 0);
    const totalCountDiff = comparison.reduce((sum, s) => sum + Math.abs(s.diffCount), 0);
    // Use 0.01 tolerance for floating point math and ensure count diff is exactly 0
    return totalAmountDiff >= 0.01 || totalCountDiff !== 0;
  }, [comparison]);

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title>
          <i className="fas fa-file-invoice-dollar mr-2 text-primary"></i>
          Verify Statement Parsing
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0">
        <div className="p-3 bg-white border-bottom">
           <div className="row text-center">
              <div className="col-4 border-right">
                <div className="small text-muted text-uppercase font-weight-bold">Statement Date</div>
                <div className="h6 mb-0">{reconcileData.statementDate}</div>
              </div>
              <div className="col-4 border-right">
                <div className="small text-muted text-uppercase font-weight-bold">Beginning Balance</div>
                <div className="h6 mb-0">{formatCurrency(reconcileData.beginningBalance)}</div>
              </div>
              <div className="col-4">
                <div className="small text-muted text-uppercase font-weight-bold">Ending Balance</div>
                <div className="h6 mb-0 text-primary font-weight-bold">{formatCurrency(reconcileData.endingBalance)}</div>
              </div>
           </div>
        </div>

        <Table responsive hover className="mb-0">
          <thead className="bg-light">
            <tr>
              <th className="border-0 small text-uppercase">Section</th>
              <th className="border-0 small text-uppercase text-center">Statement Baseline</th>
              <th className="border-0 small text-uppercase text-center">Parsed Results</th>
              <th className="border-0 small text-uppercase text-right">Difference</th>
            </tr>
          </thead>
          <tbody>
            {comparison.map(s => (
              <tr key={s.key}>
                <td className="align-middle font-weight-bold small">{s.label}</td>
                <td className="text-center align-middle">
                    <div className="small font-weight-bold">{s.baseline.count} items</div>
                    <div className="extra-small text-muted">{formatCurrency(s.baseline.amount)}</div>
                </td>
                <td className="text-center align-middle">
                    <div className="small font-weight-bold">{s.parsed.count} items</div>
                    <div className="extra-small text-muted">{formatCurrency(s.parsed.amount)}</div>
                </td>
                <td className="text-right align-middle">
                    {s.diffCount === 0 && Math.abs(s.diffAmount) < 0.01 ? (
                        <Badge variant="success"><i className="fas fa-check"></i> Matched</Badge>
                    ) : (
                        <div className="text-danger font-weight-bold small">
                            {s.diffCount !== 0 && <span>{s.diffCount > 0 ? '+' : ''}{s.diffCount} items<br/></span>}
                            {Math.abs(s.diffAmount) >= 0.01 && <span>{formatCurrency(s.diffAmount)}</span>}
                        </div>
                    )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        {!hasDiscrepancy ? (
            <div className="alert alert-success m-3 mb-4 py-2 small">
                <i className="fas fa-check-circle mr-2"></i>
                <strong>Great!</strong> The parsed transactions match the statement summary perfectly.
            </div>
        ) : (
            <div className="alert alert-warning m-3 mb-4 py-2 small">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                <strong>Discrepancy Detected:</strong> The individual transactions parsed do not match the bank's summary table. You may need to manually add missing items or edit the amounts.
            </div>
        )}
      </Modal.Body>
      <Modal.Footer className="bg-light">
        <Button variant="secondary" onClick={onHide}>Cancel</Button>
        <Button variant="primary" onClick={onConfirm}>
            {!hasDiscrepancy ? 'Continue to Reconciliation' : 'Continue Anyway'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default StatementSummaryModal;
